import { useState, useEffect } from 'react'
import { useSearchDestinatari, useCreateDestinatario, useDestinatario } from '@/hooks/useOrdini'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { CheckIcon, ChevronUpDownIcon, PlusIcon, UserIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline'
import { cn } from '@/lib/utils'

/**
 * Componente per selezione destinatario con autocomplete e creazione inline.
 * 
 * FASE 7.4: Spedizioni Avanzate
 * 
 * Features:
 * - Autocomplete con ricerca server-side
 * - Creazione nuovo destinatario inline
 * - Visualizzazione tipo (privato/azienda)
 * - Validazione campi obbligatori
 * 
 * @param {Object} props
 * @param {number|null} props.value - ID destinatario selezionato
 * @param {Function} props.onChange - Callback quando cambia selezione
 * @param {string} props.error - Messaggio errore validazione
 */
const emptyAddress = () => ({
  etichetta: 'Principale',
  via: '',
  civico: '',
  cap: '',
  citta: '',
  provincia: '',
  nazione: 'IT',
  note: '',
  predefinito: true,
})
const EMPTY_DESTINATARI = []

export default function DestinatarioSelector({ value, onChange, error }) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [selectedDestinatario, setSelectedDestinatario] = useState(null)

  // Form nuovo destinatario
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    azienda: '',
    telefono: '',
    email: '',
    note: '',
    indirizzi: [emptyAddress()],
  })

  const { data: searchResults } = useSearchDestinatari(search)
  const { data: destinatarioSelezionato } = useDestinatario(value)
  const createMutation = useCreateDestinatario()

  const destinatari = searchResults || EMPTY_DESTINATARI

  // Trova destinatario selezionato
  useEffect(() => {
    if (!value) {
      setSelectedDestinatario(null)
      return
    }
    if (destinatarioSelezionato) {
      setSelectedDestinatario((current) => {
        if (
          current?.id === destinatarioSelezionato.id
          && current?.updated_at === destinatarioSelezionato.updated_at
        ) {
          return current
        }
        return destinatarioSelezionato
      })
      return
    }
    if (destinatari.length > 0) {
      const found = destinatari.find(d => d.id === value)
      if (found) {
        setSelectedDestinatario(found)
      }
    }
  }, [value, destinatari, destinatarioSelezionato])

  const handleSelect = (destinatario) => {
    setSelectedDestinatario(destinatario)
    onChange(destinatario.id, destinatario)
    setOpen(false)
  }

  const openCreateDialog = () => {
    setFormData((current) => ({
      ...current,
      nome: current.nome || search,
    }))
    setOpen(false)
    setShowCreateDialog(true)
  }

  const handleCreateNew = () => {
    // Validazione
    if (!formData.nome && !formData.azienda) {
      return
    }
    if (!formData.telefono) {
      return
    }

    createMutation.mutate(formData, {
      onSuccess: (response) => {
        const newDestinatario = response.data
        setSelectedDestinatario(newDestinatario)
        onChange(newDestinatario.id, newDestinatario)
        setShowCreateDialog(false)
        // Reset form
        setFormData({
          nome: '',
          cognome: '',
          azienda: '',
          telefono: '',
          email: '',
          note: '',
          indirizzi: [emptyAddress()],
        })
      },
    })
  }

  return (
    <div className="space-y-2">
      <Label>
        Destinatario <span className="text-destructive">*</span>
      </Label>
      
      <div className="flex gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-label="Seleziona destinatario"
              aria-expanded={open}
              className={cn(
                "flex-1 justify-between",
                error && "border-destructive"
              )}
            >
              {selectedDestinatario ? (
                <div className="flex items-center gap-2">
                  {selectedDestinatario.azienda ? (
                    <BuildingOfficeIcon className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <UserIcon className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span>{selectedDestinatario.nome_completo}</span>
                </div>
              ) : (
                "Seleziona destinatario..."
              )}
              <ChevronUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[400px] p-0">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Cerca destinatario..."
                value={search}
                onValueChange={setSearch}
              />
              <CommandList>
                <CommandEmpty>
                  {search.length < 2 ? (
                    "Digita almeno 2 caratteri per cercare"
                  ) : (
                    <div className="space-y-3 p-2">
                      <p>Nessun destinatario trovato per “{search}”</p>
                      <Button type="button" variant="outline" className="w-full" onClick={openCreateDialog}>
                        <PlusIcon className="mr-2 h-4 w-4" />
                        Crea nuovo destinatario
                      </Button>
                    </div>
                  )}
                </CommandEmpty>
                <CommandGroup>
                  {destinatari.map((destinatario) => (
                    <CommandItem
                      key={destinatario.id}
                      value={destinatario.id.toString()}
                      onSelect={() => handleSelect(destinatario)}
                    >
                      <CheckIcon
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === destinatario.id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex items-center gap-2 flex-1">
                        {destinatario.azienda ? (
                          <BuildingOfficeIcon className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <UserIcon className="h-4 w-4 text-muted-foreground" />
                        )}
                        <div className="flex-1">
                          <div className="font-medium">{destinatario.nome_completo}</div>
                          <div className="text-sm text-muted-foreground">
                            {destinatario.telefono}
                          </div>
                        </div>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label="Crea nuovo destinatario"
          title="Crea nuovo destinatario"
          onClick={openCreateDialog}
        >
          <PlusIcon className="h-4 w-4" />
        </Button>
      </div>

      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      {/* Dialog creazione nuovo destinatario */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuovo Destinatario</DialogTitle>
            <DialogDescription>
              Crea un nuovo destinatario per questa spedizione
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Nome */}
              <div className="space-y-2">
                <Label htmlFor="nome">
                  Nome {!formData.azienda && <span className="text-destructive">*</span>}
                </Label>
                <Input
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Mario"
                />
              </div>

              {/* Cognome */}
              <div className="space-y-2">
                <Label htmlFor="cognome">Cognome</Label>
                <Input
                  id="cognome"
                  value={formData.cognome}
                  onChange={(e) => setFormData({ ...formData, cognome: e.target.value })}
                  placeholder="Rossi"
                />
              </div>
            </div>

            {/* Azienda */}
            <div className="space-y-2">
              <Label htmlFor="azienda">
                Azienda {!formData.nome && <span className="text-destructive">*</span>}
              </Label>
              <Input
                id="azienda"
                value={formData.azienda}
                onChange={(e) => setFormData({ ...formData, azienda: e.target.value })}
                placeholder="XYZ Srl"
              />
              <p className="text-xs text-muted-foreground">
                Specificare almeno nome o azienda
              </p>
            </div>

            {/* Telefono */}
            <div className="space-y-2">
              <Label htmlFor="telefono">
                Telefono <span className="text-destructive">*</span>
              </Label>
              <Input
                id="telefono"
                value={formData.telefono}
                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                placeholder="+39 333 1234567"
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="mario.rossi@email.com"
              />
            </div>

            <div className="border-t pt-4 space-y-4">
              <div>
                <h4 className="font-medium">Indirizzo principale</h4>
                <p className="text-xs text-muted-foreground">
                  Verrà proposto automaticamente quando prepari la spedizione.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="destinatario_via">Via/Piazza *</Label>
                  <Input
                    id="destinatario_via"
                    value={formData.indirizzi[0].via}
                    onChange={(e) => setFormData({
                      ...formData,
                      indirizzi: [{ ...formData.indirizzi[0], via: e.target.value }],
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="destinatario_civico">Civico</Label>
                  <Input
                    id="destinatario_civico"
                    value={formData.indirizzi[0].civico}
                    onChange={(e) => setFormData({
                      ...formData,
                      indirizzi: [{ ...formData.indirizzi[0], civico: e.target.value }],
                    })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="destinatario_cap">CAP *</Label>
                  <Input
                    id="destinatario_cap"
                    value={formData.indirizzi[0].cap}
                    maxLength={10}
                    onChange={(e) => setFormData({
                      ...formData,
                      indirizzi: [{ ...formData.indirizzi[0], cap: e.target.value }],
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="destinatario_citta">Città *</Label>
                  <Input
                    id="destinatario_citta"
                    value={formData.indirizzi[0].citta}
                    onChange={(e) => setFormData({
                      ...formData,
                      indirizzi: [{ ...formData.indirizzi[0], citta: e.target.value }],
                    })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="destinatario_provincia">Provincia</Label>
                  <Input
                    id="destinatario_provincia"
                    value={formData.indirizzi[0].provincia}
                    maxLength={2}
                    onChange={(e) => setFormData({
                      ...formData,
                      indirizzi: [{ ...formData.indirizzi[0], provincia: e.target.value.toUpperCase() }],
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="destinatario_nazione">Nazione *</Label>
                  <Input
                    id="destinatario_nazione"
                    value={formData.indirizzi[0].nazione}
                    maxLength={2}
                    onChange={(e) => setFormData({
                      ...formData,
                      indirizzi: [{ ...formData.indirizzi[0], nazione: e.target.value.toUpperCase() }],
                    })}
                  />
                </div>
              </div>
            </div>

            {/* Note */}
            <div className="space-y-2">
              <Label htmlFor="note">Note Consegna</Label>
              <Textarea
                id="note"
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                placeholder='Es: "Suonare al citofono"'
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
            >
              Annulla
            </Button>
            <Button
              type="button"
              onClick={handleCreateNew}
              disabled={
                createMutation.isPending ||
                (!formData.nome && !formData.azienda) ||
                !formData.telefono ||
                !formData.indirizzi[0].via ||
                !formData.indirizzi[0].cap ||
                !formData.indirizzi[0].citta ||
                !formData.indirizzi[0].nazione
              }
            >
              {createMutation.isPending ? 'Creazione...' : 'Crea'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
